import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Book, ChevronDown, ChevronUp, Shield, MapPin, Moon, Bus, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = "https://safesheatlas.com/api";

const SafetyGuideDisplay = ({ destinationId }) => {
  const [guideData, setGuideData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState('general');

  useEffect(() => {
    fetchSafetyGuide();
  }, [destinationId]);

  const fetchSafetyGuide = async () => {
    try {
      const response = await axios.get(`${API}/guide/${destinationId}`);
      setGuideData(response.data);
    } catch (error) {
      console.error('Error fetching safety guide:', error);
    } finally {
      setLoading(false);
    }
  };

  const categoryIcons = {
    general: <Shield className="w-5 h-5" />,
    accommodation: <MapPin className="w-5 h-5" />,
    transportation: <Bus className="w-5 h-5" />,
    nightlife: <Moon className="w-5 h-5" />,
    emergency: <AlertTriangle className="w-5 h-5" />
  };

  const categoryColors = {
    general: 'bg-purple-100 text-purple-700',
    accommodation: 'bg-blue-100 text-blue-700',
    transportation: 'bg-green-100 text-green-700',
    nightlife: 'bg-indigo-100 text-indigo-700',
    emergency: 'bg-red-100 text-red-700'
  };

  if (loading) {
    return <div className="text-center py-8">Loading safety guide...</div>;
  }

  if (!guideData || !guideData.guides) {
    return <div className="text-center py-8 text-gray-500">No safety guide available</div>;
  }

  return (
    <div className="space-y-4" data-testid="safety-guide">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-3">
          <Book className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Comprehensive Safety Guide</h2>
        </div>
        <p className="text-sm opacity-90">
          AI-powered safety recommendations for {guideData.destination.city}
        </p>
        <div className="mt-4 inline-block bg-white/20 backdrop-blur px-4 py-2 rounded-full">
          <span className="text-lg font-bold">Safety Score: {Math.round(guideData.safety_score)}/100</span>
        </div>
      </div>

      {/* Guide Sections */}
      <div className="space-y-3">
        {Object.entries(guideData.guides).map(([category, guides]) => (
          <div key={category} className="bg-white rounded-xl shadow-md overflow-hidden">
            <button
              onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${categoryColors[category]}`}>
                  {categoryIcons[category]}
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-lg capitalize">{category.replace('_', ' ')}</h3>
                  <p className="text-sm text-gray-600">{guides.length} tips available</p>
                </div>
              </div>
              {expandedCategory === category ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedCategory === category && (
              <div className="px-6 pb-6 space-y-4">
                {guides.map((guide, idx) => (
                  <div key={idx} className="border-t border-gray-200 pt-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{guide.title}</h4>
                    <p className="text-sm text-gray-700 mb-3">{guide.content}</p>
                    
                    {guide.tips && (
                      <ul className="space-y-2">
                        {JSON.parse(guide.tips).map((tip, tipIdx) => (
                          <li key={tipIdx} className="flex items-start gap-2 text-sm text-gray-600">
                            <span className="text-purple-600 mt-1">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SafetyGuideDisplay;